import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Building2 } from "lucide-react";
import { b2cHref, CURRENT_BRAND } from "../config/brand";

const LOGO_URL = "/amazonico-logo.png";

// Cinematic wine + food background video (AI-generated via Sora 2, served locally from /public).
// Cache-buster to bust CDN/browser cache when the file is regenerated.
const BG_VIDEO = "/splash-bg.mp4?v=" + (process.env.REACT_APP_BUILD_ID || "20260709-ai");
const BG_POSTER = "https://images.pexels.com/photos/34736555/pexels-photo-34736555.jpeg";

/**
 * Splash gate — cinematic background video, centered heading with fade-in,
 * two CTAs (B2C / B2B) that slide up on entry. Clean, editorial, essential.
 * Black gradient overlay softens the video into darkness at the edges.
 */
export default function Splash() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="splash-page" className="relative min-h-screen w-full bg-black text-white overflow-hidden">
      {/* ============ BACKGROUND VIDEO ============ */}
      <div className="absolute inset-0">
        <img src={BG_POSTER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <video
          src={BG_VIDEO}
          poster={BG_POSTER}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Black gradient overlay — sfumato ai bordi verso il nero */}
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.95) 100%)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90" />
      </div>

      {/* ============ TOP BAR ============ */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <img src={LOGO_URL} alt="Amazonico 3000" className={`h-14 md:h-16 object-contain transition-all duration-1000 ${ready ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`} />
          <div className={`hidden md:flex items-center gap-4 text-[10px] uppercase tracking-[0.35em] text-white/70 font-semibold transition-all duration-1000 delay-200 ${ready ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
            <span>Italia → Mondo</span>
            <span className="h-px w-8 bg-[#C9A961]/60" />
            <span className="text-[#C9A961]">ExportTech</span>
          </div>
        </div>
      </header>

      {/* ============ CENTER STAGE ============ */}
      <main className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        {/* Eyebrow */}
        <div className={`text-[10px] uppercase tracking-[0.5em] text-[#C9A961] font-bold mb-6 transition-all duration-1000 delay-300 ${ready ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
          Amazonico 3000
        </div>

        {/* Heading — letter-by-letter animation via word split */}
        <h1 className="editorial-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl">
          <AnimatedWord text="Come vuoi" ready={ready} delayBase={400} className="block text-white" />
          <AnimatedWord text="iniziare?" ready={ready} delayBase={700} className="block italic text-[#C9A961] mt-2" />
        </h1>

        {/* Divider */}
        <div className={`mt-10 h-px w-24 bg-[#C9A961]/50 transition-all duration-1000 delay-[1100ms] ${ready ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`} />

        {/* CTA row */}
        <div className={`mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-[1300ms] ${ready ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <CtaButton
            to={b2cHref()}
            external={CURRENT_BRAND.b2cLinkExternal}
            testid="splash-b2c"
            icon={ShoppingBag}
            label="Entra come Cliente"
            sub="Food & Wine · Storefront"
            variant="light"
          />
          <CtaButton
            to="/business"
            testid="splash-b2b"
            icon={Building2}
            label="Entra come Azienda"
            sub="B2B Affiliate · ExportTech"
            variant="gold"
          />
        </div>
      </main>

      {/* ============ FOOTER STRIP ============ */}
      <div className={`absolute bottom-0 inset-x-0 z-30 transition-opacity duration-1000 delay-[1500ms] ${ready ? "opacity-100" : "opacity-0"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-white/50">
          <div>© {new Date().getFullYear()} Amazonico 3000</div>
          <div className="flex gap-6">
            <Link to="/login" className="hover:text-[#C9A961] transition-colors">Accedi</Link>
            <Link to="/register" className="hover:text-[#C9A961] transition-colors">Registrati</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedWord({ text, ready, delayBase, className = "" }) {
  const chars = Array.from(text);
  return (
    <span className={className}>
      {chars.map((c, i) => (
        <span
          key={i}
          className="inline-block transition-all"
          style={{
            opacity: ready ? 1 : 0,
            transform: ready ? "translateY(0px)" : "translateY(28px)",
            transitionDuration: "900ms",
            transitionDelay: `${delayBase + i * 45}ms`,
            transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)",
            whiteSpace: c === " " ? "pre" : "normal",
          }}
        >
          {c}
        </span>
      ))}
    </span>
  );
}

function CtaButton({ to, testid, icon: Icon, label, sub, variant, external }) {
  const styles = variant === "gold"
    ? "bg-[#C9A961] hover:bg-white text-black border border-[#C9A961]"
    : "bg-white/5 hover:bg-white text-white hover:text-black border border-white/40 backdrop-blur";
  const content = (
    <>
      <Icon className="h-6 w-6 shrink-0 transition-transform group-hover:scale-110" strokeWidth={1.4} />
      <div className="flex-1 text-left">
        <div className="text-[11px] uppercase tracking-[0.28em] font-bold leading-tight">{label}</div>
        <div className={`text-[9px] uppercase tracking-[0.22em] mt-1 ${variant === "gold" ? "text-black/60 group-hover:text-black/70" : "text-white/60 group-hover:text-black/60"}`}>{sub}</div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
    </>
  );
  const className = `group inline-flex items-center gap-5 px-7 md:px-9 py-5 min-w-[280px] transition-all duration-500 ${styles}`;
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={className}>
      {content}
    </Link>
  );
}
